/**
 * @file Recalls AU/NZ phone-number validation helpers.
 */

export const RECALL_PHONE_VALIDATION_MESSAGE =
    "Enter an Australian or New Zealand phone number.";

const PHONE_ALLOWED_PATTERN = /^\+?[0-9\s().-]+$/;

function sanitizeRecallPhoneValue(value) {
    return String(value || "")
        .replace(/[^\d\s().+-]/g, "")
        .replace(/(?!^)\+/g, "");
}

function getPhoneDigits(value) {
    const digits = String(value || "").replace(/\D+/g, "");
    if (digits.startsWith("0061")) {return `61${digits.slice(4)}`;}
    if (digits.startsWith("0064")) {return `64${digits.slice(4)}`;}
    return digits;
}

function isAustralianPhoneDigits(digits) {
    if (/^04\d{8}$/.test(digits)) {return true;}
    if (/^0[2378]\d{8}$/.test(digits)) {return true;}
    return /^61[23478]\d{8}$/.test(digits);
}

function isNewZealandPhoneDigits(digits) {
    if (/^02\d{7,9}$/.test(digits)) {return true;}
    if (/^0[34679]\d{7}$/.test(digits)) {return true;}
    return /^642\d{7,9}$/.test(digits) ||
        /^64[34679]\d{7}$/.test(digits);
}

/**
 * Returns whether a phone number matches supported Australian or NZ formats.
 * @param {*} value Raw phone value.
 * @returns {boolean} True when blank or a supported phone value.
 */
export function isValidRecallPhoneNumber(value) {
    const normalized = String(value || "").trim();
    if (!normalized) {return true;}
    if (!PHONE_ALLOWED_PATTERN.test(normalized)) {return false;}

    const digits = getPhoneDigits(normalized);
    return isAustralianPhoneDigits(digits) || isNewZealandPhoneDigits(digits);
}

/**
 * Applies Recalls phone validation to a native-backed input.
 * @param {HTMLInputElement|null} input Phone input.
 * @returns {boolean} Whether the field is currently valid.
 */
export function validateRecallPhoneInput(input) {
    if (!(input instanceof HTMLInputElement)) {return true;}
    const valid = isValidRecallPhoneNumber(input.value);
    input.setCustomValidity(valid ? "" : RECALL_PHONE_VALIDATION_MESSAGE);
    return valid;
}

/**
 * Sanitizes a Recalls phone input to phone-number characters only.
 * @param {HTMLInputElement|null} input Phone input.
 * @returns {void}
 */
export function sanitizeRecallPhoneInput(input) {
    if (!(input instanceof HTMLInputElement)) {return;}
    const original = input.value;
    const sanitized = sanitizeRecallPhoneValue(original);
    if (sanitized !== original) {
        const cursor = input.selectionStart;
        input.value = sanitized;
        if (typeof cursor === "number") {
            const beforeCursor = sanitizeRecallPhoneValue(original.slice(0, cursor));
            input.setSelectionRange(beforeCursor.length, beforeCursor.length);
        }
    }
    validateRecallPhoneInput(input);
}

/**
 * Binds phone-character sanitizing to Recalls phone inputs.
 * @param {Document|HTMLElement} scope Root containing phone inputs.
 * @returns {void}
 */
export function bindRecallPhoneInputSanitizer(scope) {
    if (!scope) {return;}
    scope.querySelectorAll?.('input[name="phone"]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) {return;}
        if (input.dataset.recallPhoneSanitizerBound === "true") {
            sanitizeRecallPhoneInput(input);
            return;
        }
        input.dataset.recallPhoneSanitizerBound = "true";
        input.addEventListener("input", () => sanitizeRecallPhoneInput(input));
        input.addEventListener("change", () => sanitizeRecallPhoneInput(input));
        sanitizeRecallPhoneInput(input);
    });
}
